import { useEffect, useRef, useState } from 'react';

const strengths = [
  {
    tag: 'Generative AI',
    title: '생성형 AI 기반\n업무 프로세스',
    desc: '복잡한 지반 데이터를 AI가 자동 해석하여 전문가 수준의 보고서를 즉시 생성합니다.',
  },
  {
    tag: 'Smart Monitoring',
    title: '스마트 계측 및\n자동화 기술',
    desc: 'IoT 센서와 자동화 시스템으로 현장 데이터를 실시간으로 수집·분석합니다.',
  },
  {
    tag: 'Data Integration',
    title: '데이터 통합\n기반 분석',
    desc: '지반조사, 계측, 안전진단 데이터를 하나의 플랫폼에서 통합 관리합니다.',
  },
  {
    tag: 'Predictive AI',
    title: 'AI 기반 예측 및\n의사결정 지원',
    desc: '축적된 데이터와 AI 모델로 지반 거동을 예측하고 최적 의사결정을 지원합니다.',
  },
];

const StrengthSection = () => {
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-28 bg-[#F7F8F9]">
      <div className="max-w-7xl mx-auto px-8 md:px-16">
        {/* Header row */}
        <div
          className={`flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-1 bg-[#E8521A] rounded-full" />
              <span
                className="text-[#E8521A] text-sm font-bold tracking-widest uppercase"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Core Strengths
              </span>
            </div>
            <h2
              className="text-4xl md:text-5xl font-black text-[#1B2A4A]"
              style={{ fontFamily: "'Noto Sans KR', sans-serif", letterSpacing: '-0.03em' }}
            >
              SG의 핵심 역량
            </h2>
          </div>

          {/* PDF Download Button */}
          <a
            href="https://drive.google.com/uc?export=download&id=18mofMKpKrG1WwsVLE2_eZUOVUkv3zJqG"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-white border-2 border-[#1B2A4A]/20 text-[#1B2A4A] font-bold text-[17px] px-8 py-4 rounded-xl hover:border-[#E8521A]/50 hover:text-[#E8521A] transition-all duration-200 whitespace-nowrap shrink-0 cursor-pointer"
          >
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-file-pdf-2-line text-[#E8521A] text-2xl" />
            </div>
            SG 핵심 프로세스 PDF 다운로드
          </a>
        </div>

        {/* Strength Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {strengths.map((item, index) => (
            <div
              key={item.tag}
              onMouseEnter={() => setHovered(index)}
              onMouseLeave={() => setHovered(null)}
              className={`relative bg-white border-2 rounded-2xl p-8 cursor-pointer transition-all duration-300 ${
                hovered === index ? 'border-[#E8521A]' : 'border-gray-100'
              } ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${index * 120}ms` }}
            >
              {/* Top accent bar on hover */}
              <div
                className={`absolute top-0 left-0 right-0 h-1 rounded-t-2xl transition-all duration-300 ${
                  hovered === index ? 'bg-[#E8521A]' : 'bg-transparent'
                }`}
              />

              {/* Tag */}
              <div className="mb-5">
                <span
                  className={`text-xs font-black tracking-widest uppercase transition-colors duration-300 ${
                    hovered === index ? 'text-[#E8521A]' : 'text-[#E8521A]/60'
                  }`}
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {item.tag}
                </span>
              </div>

              {/* Title */}
              <h3
                className={`text-[22px] font-black leading-tight whitespace-pre-line mb-5 transition-colors duration-300 ${
                  hovered === index ? 'text-[#E8521A]' : 'text-[#1B2A4A]'
                }`}
                style={{ fontFamily: "'Noto Sans KR', sans-serif", letterSpacing: '-0.02em' }}
              >
                {item.title}
              </h3>

              {/* Divider */}
              <div
                className={`w-10 h-0.5 rounded-full mb-5 transition-colors duration-300 ${
                  hovered === index ? 'bg-[#E8521A]' : 'bg-[#1B2A4A]/15'
                }`}
              />

              {/* Desc */}
              <p
                className="text-[14px] text-[#1B2A4A]/65 font-semibold leading-relaxed"
                style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
              >
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StrengthSection;
